import React, { useState, useEffect, useRef } from 'react';
import './AdCreationModal.scss';
import PreparingContent from './PreparingContent';
import PlatformSelection from './PlatformSelection';
import BannerEditorV2 from './BannerEditorV2';

interface AdCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  firmId?: string;
}

type WorkflowStep = 'preparing' | 'platform' | 'editor';

interface FirmDetails {
  firm_id: string;
  business_name: string;
  name?: string;
  industry?: string;
  business_description: string;
  products_services: string;
  target_audience: string[];
  unique_strengths: string;
  brand_colors: string[];
  dominant_color?: string;
  font_preferences: string[];
  dominant_font?: string;
  recommended_objective?: string;
  recommended_tone?: string;
  recommended_target_audience?: string[];
}

interface ProcessingStatus {
  firm_id: string;
  status: string;
  message: string;
  current_step?: string;
  progress_percentage: number;
  has_firm_details: boolean;
  has_visual_insights: boolean;
  has_images: boolean;
}

interface AdFormData {
  ad_platform: string[];
  ad_objective: string;
  language_and_tone: string;
  target_audience: string[];
  ad_motive?: string;
  ad_language: string;
  offers_promotions?: string;
}

interface ColorSchemeElement {
  element: string;
  color: string;
}

interface Typography {
  headline_font?: string;
  body_font?: string;
  headline_size?: string;
  body_size?: string;
}

interface GeneratedContent {
  headline?: string;
  ad_copy?: string;
  featured_benefit?: string;
  highlighted_offer?: string;
  cta?: string;
  hashtags?: string[];
  color_scheme?: ColorSchemeElement[];
  recommended_font?: string;
  story_emotion?: string;
  typography?: Typography;
}

interface ImageRecommendation {
  url: string;
  alt_text?: string;
  score?: number;
  rationale?: string;
}

const AdCreationModal: React.FC<AdCreationModalProps> = ({ isOpen, onClose, firmId = 'FIRM001' }) => {
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('preparing');
  const [firmDetails, setFirmDetails] = useState<FirmDetails | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [adFormData, setAdFormData] = useState<AdFormData | null>(null);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [recommendedImages, setRecommendedImages] = useState<ImageRecommendation[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const apiUrlBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('preparing');
      setFirmDetails(null);
      setProcessingStatus(null);
      setError(null);
      fetchFirmDetails();
    } else {
      // Cleanup polling on close
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  const fetchFirmDetails = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiUrlBase}v2/firms/${firmId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch firm details');
      }
      
      const data: FirmDetails = await response.json();
      setFirmDetails(data);
      
      // Check if processing is needed by polling status
      pollProcessingStatus();
    } catch (err) {
      console.error('Error fetching firm details:', err);
      setError('Failed to load firm details');
      setIsLoading(false);
    }
  };

  const pollProcessingStatus = async () => {
    try {
      const response = await fetch(`${apiUrlBase}v2/firms/${firmId}/processing-status`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch processing status');
      }
      
      const status: ProcessingStatus = await response.json();
      setProcessingStatus(status);
      
      // If processing is complete, move to next step
      if (status.status === 'completed' && status.progress_percentage === 100) {
        setIsLoading(false);
        setTimeout(() => {
          setCurrentStep('platform');
        }, 1000);
        
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (status.status === 'processing') {
        // Continue polling every 2 seconds
        if (!pollingIntervalRef.current) {
          pollingIntervalRef.current = setInterval(() => {
            pollProcessingStatus();
          }, 2000);
        }
      } else {
        // Data is ready, move to next step
        setIsLoading(false);
        setTimeout(() => {
          setCurrentStep('platform');
        }, 1000);
      }
    } catch (err) {
      console.error('Error polling processing status:', err);
      // Don't show error for polling failures, just stop polling
      setIsLoading(false);
      setTimeout(() => {
        setCurrentStep('platform');
      }, 2000);
    }
  };

  const handleFormSubmit = async (formData: AdFormData) => {
    setAdFormData(formData);
    setIsLoading(true);
    setError(null);
    
    try {
      // Generate content
      const contentResponse = await fetch(`${apiUrlBase}v2/firms/${firmId}/ads/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firm_id: firmId,
          ...formData
        })
      });
      
      if (!contentResponse.ok) {
        throw new Error('Failed to generate content');
      }
      
      const content: GeneratedContent = await contentResponse.json();
      setGeneratedContent(content);
      
      // Get image recommendations
      const imageResponse = await fetch(`${apiUrlBase}v2/firms/${firmId}/ads/recommend-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firm_id: firmId,
          ...formData
        })
      });
      
      if (!imageResponse.ok) {
        throw new Error('Failed to get image recommendations');
      }
      
      const imageData = await imageResponse.json();
      const images: ImageRecommendation[] = imageData.data?.recommendations || imageData.recommendations || [];
      setRecommendedImages(images);
      
      // Set first/highest scored image as selected
      if (images.length > 0) {
        setSelectedImage(images[0].url);
      }
      
      setIsLoading(false);
      setCurrentStep('editor');
    } catch (err) {
      console.error('Error generating ad content:', err);
      setError('Failed to generate advertisement content');
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    console.log('Download ad creative');
    // TODO: Implement download functionality
  };

  const handlePublish = async () => {
    if (!adFormData || !generatedContent) {
      return;
    }
    
    try {
      const response = await fetch(`${apiUrlBase}v2/firms/${firmId}/ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firm_id: firmId,
          ...adFormData,
          selected_image_url: selectedImage,
          generated_content: generatedContent
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save advertisement');
      }
      
      console.log('Advertisement published successfully');
      onClose();
    } catch (err) {
      console.error('Error publishing ad:', err);
      setError('Failed to publish advertisement');
    }
  };

  const handleModalClose = () => {
    if (currentStep === 'preparing') return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="ad-creation-modal-overlay" onClick={handleModalClose}>
      <div className="ad-creation-modal" onClick={(e) => e.stopPropagation()}>
        {currentStep !== 'preparing' && (
          <button className="modal-close-btn" onClick={handleModalClose}>
            X
          </button>
        )}

        <div className="modal-content">
          {error && (
            <div className="error-message" style={{color: 'red', padding: '1rem', textAlign: 'center'}}>
              {error}
            </div>
          )}
          
          {currentStep === 'preparing' && (
            <PreparingContent 
              status={processingStatus?.message || 'Preparing your advertisement...'}
              progress={processingStatus?.progress_percentage || 0}
            />
          )}
          
          {currentStep === 'platform' && firmDetails && (
            <PlatformSelection
              firmDetails={firmDetails}
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
            />
          )}
          
          {currentStep === 'editor' && generatedContent && (
            <BannerEditorV2
              generatedContent={generatedContent}
              recommendedImages={recommendedImages}
              selectedImage={selectedImage}
              onImageSelect={setSelectedImage}
              firmDetails={firmDetails}
              onDownload={handleDownload}
              onPublish={handlePublish}
              selectedPlatforms={adFormData?.ad_platform || []}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdCreationModal;
